import { Heading } from '@mm/components';
import * as allPermissions from '@mm/constants/facebook-permissions';
import { ErrorReportingUtils } from '@mm/plugins';
import chatbotCRUDActions from '@mm/state/actions/ChatbotCRUDActions';
import connectedPageCRUDActions, {
  useConnectedPageCRUDActions,
} from '@mm/state/actions/ConnectedPageCRUDActions';
import { useFacebookSDKActions } from '@mm/state/actions/facebookActions';
import usersCRUDActions from '@mm/state/actions/UsersCRUDActions';
import DomainController from '@mm/state/controllers/DomainController';
import ChatbotModel from '@mm/state/model/ChatbotModel';
import {
  OnboardingSignupStep,
  UserAttributes,
} from '@mm/state/model/CompanyUsersModel';
import ConnectedPageModel from '@mm/state/model/ConnectedPageModel';
import { unconnectedPagesSelector } from '@mm/state/modules/modalsState/selectors';
import {
  activeCompanySelector,
  selectActiveCompanyId,
  selectCompanies,
} from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import classnames from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { withRouter } from 'react-router';
import { compose } from 'recompose';
import { Dispatch } from 'redux';
import { PrimaryButton } from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { SearchInput } from '../../../../../common/components/SearchInput/SearchInput';
import { RouterComponentProps } from '../../../../../common/helpers/routes/interfaces';
import { useInfiniteScroll } from '../../../../../common/hooks/use-infinite-scroll';
import { Logo } from '../../../../../layout/BotBuilderSidebar/Logo/Logo';
import { useCreateGetemails } from '../../../XRayOnboarding/hooks/use-create-getemails';
import OnboardingPageLoadingIndicator from '../../common/OnboardingPageLoadingIndicator/OnboardingPageLoadingIndicator';
import SignupOnboardingPage from '../../common/SignupOnboardingPage/SignupOnboardingPage';
import { useOpenOnboardingErrorModal } from '../../use-open-onboarding-error-modal';
import { useSetNextOnboardingStep } from '../../use-set-next-onboarding-step';
import './ConnectPages.pcss';
import FacebookPageConnector from './FacebookPageConnector/FacebookPageConnector';

type InnerProps = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  RouterComponentProps;

type Props = InnerProps;

// If a user has multiple companies it's an error and just skip onboarding all together
const useMultipleCompaniesEscapeHatch = (props: Props) => {
  const { companies, activeCompany } = props;
  const { setNextOnboardingStep } = useSetNextOnboardingStep();
  const multipleCompanies = companies && companies.length > 1;

  useEffect(() => {
    if (multipleCompanies) {
      ErrorReportingUtils.reportError(
        'Sent to Onboarding with multiple companies',
        {
          extraData: { companies, activeCompany },
        },
      );

      setNextOnboardingStep(OnboardingSignupStep.NONE);
    }
  }, []);
};

type CreationResponse = { facebookPage: ConnectedPageModel; bot: ChatbotModel };

const useSetupOnboardingChatbot = (props: Props) => {
  const {
    companyId,
    updateUserAttributes,
    session,
    attachToFacebookPage,
    retrieveBotById,
  } = props;
  const { openOnboardingErrorModal } = useOpenOnboardingErrorModal();
  const { setNextOnboardingStep } = useSetNextOnboardingStep();
  const [isConnectingPage, setIsConnectingPage] = useState(false);
  const sessionId = session ? session.id : null;

  const setupOnboardingChatbot = async (creationResponse: CreationResponse) => {
    setIsConnectingPage(true);

    try {
      if (
        creationResponse &&
        creationResponse.bot &&
        creationResponse.facebookPage &&
        sessionId
      ) {
        const botId = creationResponse.bot.id;
        const facebookPageId = creationResponse.facebookPage.id;

        // Domain controller only takes DomainModel so guess create fake id?
        // Does not send id to backend.
        const updatedDomain = {
          id: 2,
          connectedPageId: facebookPageId,
          domainsList: [location.origin],
        };

        await Promise.all([
          attachToFacebookPage(botId, facebookPageId),
          updateUserAttributes(companyId, sessionId, {
            onboardingSignupBot: botId,
          }),
          retrieveBotById(botId),
          DomainController.putDomain(facebookPageId, updatedDomain),
        ]);

        setNextOnboardingStep(OnboardingSignupStep.USER_INFO);
      }
    } catch (error) {
      ErrorReportingUtils.reportError('Onboarding connect page error', {
        extraData: { error },
      });
      openOnboardingErrorModal();
    }
  };

  return { setupOnboardingChatbot, isConnectingPage, setIsConnectingPage };
};

const ConnectPages = (props: Props) => {
  const {
    unconnectedPages,
    nextAvailablePages,
    fetchMorePages,
    companyId,
    clearPages,
  } = props;
  useMultipleCompaniesEscapeHatch(props);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const busy = useSelector((state: StoreState) => state.connectedPage.busy);

  const { setupOnboardingChatbot, isConnectingPage, setIsConnectingPage } =
    useSetupOnboardingChatbot(props);

  const { requestPermissionsWithManualFlowFallback } = useFacebookSDKActions();
  const { retrieveAvailablePages } = useConnectedPageCRUDActions();

  const fetchMore = useCallback(async () => {
    if (nextAvailablePages) {
      await fetchMorePages(25, nextAvailablePages, searchQuery);
    }
  }, [fetchMorePages, nextAvailablePages, searchQuery]);

  const onSuccessConnect = async (response: {
    facebookPage: ConnectedPageModel;
    bot: ChatbotModel;
  }) => {
    setupOnboardingChatbot(response);
  };

  const onSearch = async (q: string) => {
    if (!isSearching) {
      setIsSearching(true);
      setSearchQuery(q);
      clearPages();
      await fetchMorePages(25, '', q);
      setIsSearching(false);
    }
  };

  const updatePermissions = async () => {
    const permissions = allPermissions.getArrayOfPermissions(
      allPermissions.ALL_PERMISSIONS,
    );

    try {
      const res = await requestPermissionsWithManualFlowFallback(permissions);
      if (res) {
        retrieveAvailablePages();
      }
    } catch (e) {
      console.error(e);
    }
  };

  const { isFetching } = useInfiniteScroll({
    fetchMore,
    fetchDisabled: !nextAvailablePages,
    useWindow: true,
    scrollEndOffset: 200,
  });

  const isLoading = isConnectingPage || isFetching || isSearching;

  const classList = classnames('connect-pages__connected-page-list', {
    'connect-pages--no-pages': !unconnectedPages.length,
  });

  return (
    <SignupOnboardingPage className="connect-pages">
      <Logo className="connect-pages-logo" />
      <div className="connect-pages-header">
        <div className="connect-pages-title">
          Sign Up with Facebook
          <div className="connect-pages-subtitle">30-day free trial</div>
        </div>

        <div>
          <div> Connect your first Facebook Page.</div>
          Don’t worry! We won’t start sending messages for you just yet.
        </div>

        <div className="connect-pages__instructions-block">
          <Heading size={4} className="connect-pages__instructions-heading">
            Have a Facebook Page but don't see it below?
          </Heading>
          <div className="connect-pages__instructions-actions">
            <PrimaryButton
              className="connect-pages__update-permissions-button"
              onClick={updatePermissions}
              size="lg"
            >
              Update Permissions
            </PrimaryButton>
            <div>
              and follow
              <a
                className="connect-pages__instructions-link"
                target="_blank"
                rel="noopener noreferrer"
                href="http://customers.ai/help/article/dqy287mcgy-edit-your-facebook-permissions"
              >
                <strong> these instructions</strong>
              </a>
            </div>
          </div>
        </div>
        <SearchInput
          className="connect-pages__search-bar"
          onSearch={onSearch}
          placeholder="Search Page..."
          useDarkStyle
        />
      </div>

      <div className={classList}>
        {unconnectedPages.map(unconnectedPage => (
          <FacebookPageConnector
            disabled={isConnectingPage}
            key={unconnectedPage.remoteId}
            onboarding={true}
            showJoinFacebookPage={unconnectedPage.companyId !== companyId}
            pageInfo={unconnectedPage}
            onConnectBtnClick={() => setIsConnectingPage(true)}
            afterPageConnect={onSuccessConnect}
          />
        ))}

        {!busy && !unconnectedPages.length && (
          <>
            <Heading size={4} addMarginAbove>
              Don't have a Facebook Page?{' '}
            </Heading>
            <a
              href="https://www.facebook.com/pages/create/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <PrimaryButton
                className="connect-pages__update-permissions-button"
                size="lg"
              >
                Create a new Facebook Page
              </PrimaryButton>
            </a>
            <div className="connect-pages__unconnected-pages-help-wrapper">
              If you still have troubles let us know:{' '}
              <a
                href="mailto:support@customers.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="connect-pages__instructions-link"
              >
                support@customers.ai
              </a>
            </div>
          </>
        )}
      </div>

      <OnboardingPageLoadingIndicator isLoading={isLoading || busy} />
    </SignupOnboardingPage>
  );
};

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  getConnectedPages: () => dispatch(connectedPageCRUDActions.retrieveAll()),
  attachToFacebookPage: (botId: number, facebookPageId: number) =>
    dispatch(
      chatbotCRUDActions.attachToFacebookPage(
        botId,
        facebookPageId,
        false,
        true,
      ),
    ),
  clearPages: () => dispatch(connectedPageCRUDActions.clearAvailablePages()),
  fetchMorePages: (limit: number, next: string, search: string) =>
    dispatch(
      connectedPageCRUDActions.retrieveAvailablePages(limit, next, search),
    ),
  updateUserAttributes: (
    companyId: number,
    entityId: number,
    userAttributes: Partial<UserAttributes>,
  ) =>
    dispatch(
      usersCRUDActions.updateUserAttributes(
        companyId,
        entityId,
        userAttributes,
      ),
    ),
  retrieveBotById: (id: number) => dispatch(chatbotCRUDActions.retrieve(id)),
});

const mapStateToProps = (state: StoreState) => ({
  companies: selectCompanies(state),
  companyId: selectActiveCompanyId(state),
  nextAvailablePages: state.connectedPage.availablePages.next,
  activeCompany: activeCompanySelector(state),
  unconnectedPages: unconnectedPagesSelector(state),
  session: state.session.session,
});

const enhance = compose<InnerProps, {}>(
  connect(mapStateToProps, mapDispatchToProps),
  withRouter,
);

export default enhance(ConnectPages);
